"use client";
import React, { useCallback, useEffect, useRef, useState, KeyboardEvent } from "react";
import cn from "classnames";
import styles from "./HeaderSearchForm.module.scss";
import { SpriteIcon } from "components/UI/SpriteIcon";
import searchIcon from "public/img/icons/search.svg";
import searchNoneIcon from "public/img/icons/search-none.svg";
import { debounce } from "lodash";
import { api } from "utils/httpClient";
import { Loader } from "./Loader";
import { HeaderSearchResults } from "./HeaderSearchResults";
import { useAppDispatch, useAppSelector } from "store";
import { setOpen as setSearchOpen, setPending, setSearchTerm } from "store/samplesSearch/samplesSearchSlice";
import { SamplesSearchView, SampleType } from "types/shared";
import { freeMobileScroll } from "utils/helpers";
import { Button } from "@/components/UI/buttons/Button";
import { useSampleFilterContext } from "@/components/SampleFilterContext";
import { useRedirect } from "@/utils/useRedirect";

export const HeaderSearchForm = () => {
  const redirect = useRedirect();
  const [results, setResults] = useState<SampleType[] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPages, setCurrentPages] = useState(0);

  const [loading, setLoading] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const searchTermRef = useRef<string>();
  const searchInput = useRef<HTMLInputElement>(null);
  const resultsWrapRef = useRef<HTMLDivElement>(null);

  const resultsRef = useRef<SampleType[] | null>(results);
  resultsRef.current = results;

  const loadingRef = useRef<boolean>();
  loadingRef.current = loading;

  const searchTerm = useAppSelector(({ samplesSearch }) => samplesSearch.term);
  const pending = useAppSelector(({ samplesSearch }) => samplesSearch.pending);
  const isSearchOpen = useAppSelector(({ samplesSearch }) => samplesSearch.open);
  const dispatch = useAppDispatch();

  searchTermRef.current = searchTerm;

  const { selectedValues, onValueChange } = useSampleFilterContext() || {};

  const onOverlayClick = (event: React.MouseEvent) => {
    if (window.innerWidth <= 576) return;
    if (event.target === overlayRef.current) closeModal();
  };

  const closeModal = () => {
    freeMobileScroll();
    dispatch(setSearchOpen(false));
    dispatch(setSearchTerm(""));

    searchInput.current && (searchInput.current.value = "");
    setResults(null);
  };

  const adjustResultsWrapHeight = () => {
    const wrapper = resultsWrapRef.current;
    if (!wrapper) return;
    wrapper.removeAttribute("style");
    if (window.innerWidth > 576) {
      wrapper.setAttribute("style", `height: ${wrapper.offsetHeight}px`);
    }
  };

  const sendSearchRequest = useCallback(
    (value: string) => {
      if (loadingRef.current) return;
      api
        .get<SamplesSearchView>(
          "/",
          {
            params: {
              title: value,
              limit: 20,
              page: 1,
              sortByDateUpdated: "desc",
            },
          },
          { cache: "no-store" }
        )
        .then((resp) => {
          setCurrentPage(1);
          setResults(resp.items);
          setCurrentPages(resp.pagination.pages);
        })
        .catch(() => {
          setResults([]);
        })
        .finally(() => {
          dispatch(setPending(false));
          setTimeout(() => {
            adjustResultsWrapHeight();
          }, 0);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    if (currentPage === 1 || loadingRef.current) return;
    setLoading(true);
    api
      .get<SamplesSearchView>(
        "/",
        {
          params: {
            title: searchTerm,
            limit: 20,
            page: currentPage,
            sortByDateUpdated: "desc",
          },
        },
        { cache: "no-store" }
      )
      .then((resp) => {
        setResults(resultsRef.current ? [...resultsRef.current, ...resp.items] : resp.items);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  }, [currentPage, searchTerm]);

  const onChange = useCallback(
    (event: React.ChangeEvent) => {
      if (!isSearchOpen) return;
      const { value } = event.target as HTMLInputElement;
      dispatch(setSearchTerm(value));
      sendSearchRequest(value);
    },
    [dispatch, isSearchOpen, sendSearchRequest]
  );

  const onSearchInputChange = debounce(onChange, 500);

  useEffect(() => {
    if (isSearchOpen) {
      if (searchInput.current?.value === searchTerm) return;
      searchInput.current && (searchInput.current.value = searchTerm);
      if (!searchTerm) return;
      sendSearchRequest(searchTerm);
    }
  }, [sendSearchRequest, isSearchOpen, searchTerm]);

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (currentPage >= currentPages || loadingRef.current) return;
      const target = event.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const height = target.clientHeight;
      const scrollHeight = target.scrollHeight;

      if (scrollTop + height / 2 > scrollHeight / 2) {
        setCurrentPage(currentPage + 1);
      }
    },
    [currentPages, currentPage, setCurrentPage]
  );

  const onSearchResultsScroll = debounce(onScroll, 200);

  useEffect(() => {
    if (pending) {
      adjustResultsWrapHeight();
    }
  }, [pending]);

  const handleInputKeyDown = (event: KeyboardEvent) => {
    if (!searchInput.current) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onValueChange?.({ ...selectedValues, title: searchInput.current.value });
      redirect(`/search?title=${searchInput.current.value}`);

      closeModal();
      return;
    }
    dispatch(setPending(true));
  };

  return (
    <div
      className={cn(styles.overlay, {
        [styles.overlayOpen]: isSearchOpen,
      })}
      onClick={onOverlayClick}
      ref={overlayRef}
    >
      <div className={styles.inner}>
        <div className={styles.controls}>
          <div className={styles.search}>
            <button type="button" className={styles.searchBtn} onClick={() => sendSearchRequest(searchTerm)}>
              <SpriteIcon icon={searchIcon} />
            </button>
            <input
              type="search"
              placeholder="Search for samples, tools, subjects, essay types etc."
              className={styles.searchInput}
              onKeyDown={handleInputKeyDown}
              onChange={onSearchInputChange}
              ref={searchInput}
            />
          </div>
          <Button tag="button" type="accent" className={styles.cancelBtn} onClick={closeModal}>
            Cancel
          </Button>
        </div>
        {(searchTerm || pending) && (
          <div className={styles.results} ref={resultsWrapRef}>
            <div className={styles.resultsInner} onScroll={onSearchResultsScroll}>
              {pending ? (
                <Loader className={styles.loader} text="Loading" />
              ) : results?.length ? (
                <HeaderSearchResults results={results} closeModal={closeModal} />
              ) : (
                <div className={styles.notFound}>
                  <SpriteIcon icon={searchNoneIcon} className={styles.notFoundIcon} />
                  <div className={styles.notFoundText}>No results for “{searchTerm}“.</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
